import { useState, useEffect } from 'react';
import { getCurrentLogoUrl, getCurrentTheme, isDarkMode as checkDarkMode, getLogoIconUrl } from './theme.service';

/**
 * React hook to get current theme logo
 * Automatically updates when theme changes
 */
export const useThemeLogo = () => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    // Get initial logo
    const logo = getCurrentLogoUrl();
    setLogoUrl(logo);

    // Listen for theme changes (if theme is reloaded)
    const handleStorageChange = (e) => {
      if (e.key === 'currentTheme') {
        const newLogo = getCurrentLogoUrl();
        setLogoUrl(newLogo);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check on interval (for same-tab theme updates)
    const interval = setInterval(() => {
      const newLogo = getCurrentLogoUrl();
      if (newLogo !== logoUrl) {
        setLogoUrl(newLogo);
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return logoUrl;
};

/**
 * React hook to get current theme data
 */
export const useTheme = () => {
  const [theme, setTheme] = useState(null);

  useEffect(() => {
    const currentTheme = getCurrentTheme();
    setTheme(currentTheme);

    const handleStorageChange = (e) => {
      if (e.key === 'currentTheme') {
        const newTheme = getCurrentTheme();
        setTheme(newTheme);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    const interval = setInterval(() => {
      const newTheme = getCurrentTheme();
      if (JSON.stringify(newTheme) !== JSON.stringify(theme)) {
        setTheme(newTheme);
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return theme;
};

/**
 * React hook to check if current theme is dark mode
 */
export const useDarkMode = () => {
  const [darkMode, setDarkMode] = useState(true);

  useEffect(() => {
    const checkMode = () => {
      const isDark = checkDarkMode();
      setDarkMode(isDark);
    };

    checkMode();

    const handleStorageChange = (e) => {
      if (e.key === 'currentTheme') {
        checkMode();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    const interval = setInterval(checkMode, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return darkMode;
};

/**
 * React hook to get logo icon URL
 * Automatically updates when theme changes
 */
export const useThemeLogoIcon = () => {
  const [logoIconUrl, setLogoIconUrl] = useState(null);

  useEffect(() => {
    // Get initial logo icon
    const logoIcon = getLogoIconUrl();
    setLogoIconUrl(logoIcon);

    // Listen for theme changes (if theme is reloaded)
    const handleStorageChange = (e) => {
      if (e.key === 'currentTheme') {
        const newLogoIcon = getLogoIconUrl();
        setLogoIconUrl(newLogoIcon);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check on interval (for same-tab theme updates)
    const interval = setInterval(() => {
      const newLogoIcon = getLogoIconUrl();
      if (newLogoIcon !== logoIconUrl) {
        setLogoIconUrl(newLogoIcon);
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return logoIconUrl;
};

export default {
  useThemeLogo,
  useTheme,
  useDarkMode,
  useThemeLogoIcon
};

